import { ApplicationStatus, Verification } from "shared/types";
import { Applicant } from "types/application";
import { PendingReportIcon, PendingReportView } from "./PendingReportView";
import { PetScreeningReportSlice } from "../../slices/PetScreeningReportSlice";
import {
  AnimalScreeningRequestState,
  getAnimalScreeningRequestState
} from "../../slices/PetScreeningReportSlice/utils";

interface PetScreeningReportViewProps {
  verification: Verification;
  applicant: Applicant;
  applicationStatus: ApplicationStatus;
}

/*
 * An invite that has not gone out is not a report in progress. Which pre-request state
 * applies depends on when the partner sends the invite and where the application got
 * to, and each has its own copy.
 *
 * None of them gets the clock: the two still waiting on a milestone carry the amber
 * "no report yet" mark, and the two that are settled for good carry the inactive mark.
 */
const PRE_REQUEST_VIEWS: Partial<
  Record<
    AnimalScreeningRequestState,
    { message: string; title?: string; icon: PendingReportIcon }
  >
> = {
  [AnimalScreeningRequestState.AwaitingSubmission]: {
    message:
      "This report will be available after the applicant submits their application",
    icon: "awaiting"
  },
  [AnimalScreeningRequestState.AwaitingApproval]: {
    message: "This report will be available if the application is approved",
    icon: "awaiting"
  },
  [AnimalScreeningRequestState.NotNeeded]: {
    title: "Pet screening report is inactive",
    message: "The report is set to be sent after applications are approved",
    icon: "inactive"
  },
  [AnimalScreeningRequestState.NotRequested]: {
    message: "Pet screening was not requested for this application",
    icon: "inactive"
  },
  [AnimalScreeningRequestState.Canceled]: {
    title: "Pet screening was canceled",
    message:
      "The request was sent, but the application was declined before it was completed",
    icon: "inactive"
  }
};

export const PetScreeningReportView = ({
  verification,
  applicant,
  applicationStatus
}: PetScreeningReportViewProps) => {
  const requestState = getAnimalScreeningRequestState({
    verificationStatus: verification.status,
    requestedAt: verification.requested_at,
    triggerOn: verification.settings?.trigger_on,
    applicationStatus
  });

  const preRequestView = PRE_REQUEST_VIEWS[requestState];

  if (preRequestView)
    return (
      <div className="h-full mx-auto">
        <PendingReportView
          title={preRequestView.title}
          message={preRequestView.message}
          icon={preRequestView.icon}
        />
      </div>
    );

  return (
    <PetScreeningReportSlice
      applicationStatus={applicationStatus}
      verification={verification}
      applicant={applicant}
    />
  );
};
